import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { message, Form, Button } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import FormInput from '../../global/Forms/FormInput';

import catalogueTranslation from '../../../constants/CatalogueTranslations.json';
import validationMessages from '../../../constants/ValidationMessages.json';
import { fillInPlaceholders } from '../../../utils/String';
import FormCheckbox from '../../global/Forms/FormCheckbox';
import { booleanToInt } from '../../../utils/Utils';

type FormValues = {
  filterName: string;
  publicFilter: number;
};

type CatalogueSaveFilterProps = {
  handleSaveNewFilter: (name: string, publicFilter: number) => Promise<undefined | ProgressEvent>;
  canManageGlobalFilter?: boolean;
};

const CatalogueSaveFilter: React.FC<CatalogueSaveFilterProps> = props => {
  const { t } = useTranslation();

  const validationSchema = Yup.object().shape({
    filterName: Yup.string().required(validationMessages.required).min(3),
  });

  const formMethods = useForm<FormValues>({
    defaultValues: {
      filterName: '',
      publicFilter: 0,
    },
    resolver: yupResolver(validationSchema) as any,
    mode: 'onTouched',
  });

  const { handleSubmit, formState, setValue } = formMethods;
  const { isSubmitting } = formState;

  const onSubmit = async (values: FormValues) => {
    const { filterName, publicFilter } = values;
    try {
      await props.handleSaveNewFilter(filterName, publicFilter);

      message.success(
        fillInPlaceholders(catalogueTranslation.filterCreationSuccessfully, {
          filterName,
        })
      );
    } catch {
      formMethods.setError('filterName', {
        type: 'manual',
        // Workaround for backend, waiting for better error handling
        message: catalogueTranslation.filterAlreadyExists,
      });
    }
  };

  return (
    <FormProvider {...formMethods}>
      <Form
        className="save-filter-popover__form"
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
      >
        <FormInput
          name="filterName"
          label={`${catalogueTranslation.catalogueBar.filterName}:`}
          placeholder={catalogueTranslation.catalogueBar.filterName}
          required
          useHookForm
        />
        {props.canManageGlobalFilter && (
          <FormCheckbox
            name="publicFilter"
            handleChange={e => setValue('publicFilter', booleanToInt(e.target.checked) as number)}
            content={
              <div>
                {t('catalogue:makeThisVisible')} <GlobalOutlined />
              </div>
            }
            useHookForm
          />
        )}
        <Button
          className="save-filter-popover__form__submit"
          type="primary"
          disabled={isSubmitting}
          htmlType="submit"
        >
          {catalogueTranslation.catalogueBar.save}
        </Button>
      </Form>
    </FormProvider>
  );
};

export default CatalogueSaveFilter;
